import { Download } from "@mui/icons-material";
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import dayjs from "dayjs";
import { Helmet } from "react-helmet-async";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import purchaseService from "../../services/purchase";
import queryDefault from "../../utils/queryDefault";

export default function PurchaseDetail() {
  const { id } = useParams<{ id: string }>();

  const { data: purchaseData, isLoading: purchaseLoading } = useQuery(
    ["purchaseDetail", id],
    () => purchaseService.viewPurchaseDetail(id),
    {
      ...queryDefault,
      enabled: !!id,
    }
  );

  const { data: filesData, isLoading: filesLoading } = useQuery(
    ["purchaseFiles", id],
    () => purchaseService.getPurchaseFiles(Number(id)),
    {
      ...queryDefault,
      enabled: !!id,
    }
  );

  if (purchaseLoading) {
    return <Typography>Loading...</Typography>;
  }

  const purchase = purchaseData?.data;
  const files = filesData?.data || [];

  if (!purchase) {
    return <Typography>Purchase not found</Typography>;
  }

  const totalAmount = purchase.items?.reduce(
    (sum: number, item: any) => sum + (item.qty * item.price),
    0
  ) || 0;

  const handleDownloadFile = (fileId: number) => {
    purchaseService.downloadPurchaseFile(fileId);
  };

  return (
    <MainLayout>
      <Helmet>
        <title>Purchase Detail</title>
      </Helmet>

      <Breadcrumb
        label="Purchase Detail"
        breadcrumbs={[
          { label: "Dashboard", href: "/app" },
          { label: "Purchase Management", href: "/app/purchase" },
        ]}
      />

      <ProtectedPermissionPage acceptPermissions={[1, 2]}>
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Purchase Details
          </Typography>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Purchase No:
                </Typography>
                <Typography>{purchase.purchase_no}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Status:
                </Typography>
                <Typography>{purchase.status || "N/A"}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Decline Reason:
                </Typography>
                <Typography>{purchase.decline_reason || "N/A"}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Usage Date:
                </Typography>
                <Typography>
                  {purchase.usage_date ? dayjs(purchase.usage_date).format("DD/MM/YYYY") : "N/A"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Request Date:
                </Typography>
                <Typography>
                  {purchase.request_date ? dayjs(purchase.request_date).format("DD/MM/YYYY") : "N/A"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Input Link:
                </Typography>
                {purchase.input_link && purchase.input_link !== "N/A" ? (
                  <Typography
                    component="a"
                    href={purchase.input_link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {purchase.input_link}
                  </Typography>
                ) : (
                  <Typography>N/A</Typography>
                )}
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Alamat Pengiriman:
                </Typography>
                <Typography>{purchase.alamat_pengiriman || "N/A"}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Created By:
                </Typography>
                <Typography>{purchase.created_by_name || purchase.created_by}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Bank Name:
                </Typography>
                <Typography>{purchase.nama_bank || "N/A"}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Account Number:
                </Typography>
                <Typography>{purchase.no_rek || "N/A"}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Store Detail:
                </Typography>
                <Typography>{purchase.detail_toko || "N/A"}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Purchase By:
                </Typography>
                <Typography>{purchase.purchase_by || "N/A"}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Total Amount:
                </Typography>
                <Typography>IDR {totalAmount.toLocaleString()}</Typography>
              </Box>
            </Stack>
          </Paper>

          <Typography variant="h6" gutterBottom>
            Purchase Items
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Qty</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Kategori</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchase.items?.map((item: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{item.qty}</TableCell>
                    <TableCell>{item.description}</TableCell>
                    <TableCell>{item.kategori || "N/A"}</TableCell>
                    <TableCell>IDR {item.price?.toLocaleString()}</TableCell>
                    <TableCell>IDR {(item.qty * item.price)?.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={4} align="right" sx={{ fontWeight: "bold" }}>
                    Grand Total:
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    IDR {totalAmount.toLocaleString()}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Attached Files
          </Typography>
          {filesLoading ? (
            <Typography>Loading files...</Typography>
          ) : files.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>File Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {files.map((file: any, index: number) => (
                    <TableRow key={index}>
                      <TableCell>{file.file_path.split('/').pop()}</TableCell>
                      <TableCell>{file.type || "N/A"}</TableCell>
                      <TableCell>
                        <IconButton onClick={() => handleDownloadFile(file.id)}>
                          <Download />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography>No files attached</Typography>
          )}
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}